import React, { useEffect, useState } from "react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useLocation } from "react-router-dom";
import { useFlag, useVariable } from "@/services/backstage/BackstageProvider";
import { useAuthenticationService } from "@/services/authentication/authentication-service";
import { SuperAdminSettings } from "@/types/dataManagement.types";
import { GenericSettingsGrid, SettingsGridRow } from "./GenericSettingsGrid";
import { isRowDataDirty } from "./settingsGrid.utils";
import {
  BackstageFeatures,
  mapRowDataToSuperAdminSettings,
  mapSuperAdminSettingsToRowData,
} from "./superAdminSettingsGrid.utils";

ModuleRegistry.registerModules([AllCommunityModule]);

export const SuperAdminSettingsGrid: React.FC = () => {
  const location = useLocation();
  const { tenantId } = location.state as { tenantId: string };
  const [user] = useAuthenticationService((s) => [s.user]);

  const [snapshot, setSnapshot] = useState<SuperAdminSettings>();
  const [rowData, setRowData] = useState<SettingsGridRow[]>([]);
  const [initialRowData, setInitialRowData] = useState<SettingsGridRow[]>([]);

  const apiUrl = useVariable("apiUrl")?.replace("/graphql", "");
  const organisationSettingsReadUrl = `${apiUrl}/api/admin/organisation-settings`;
  const backstageFeatures: BackstageFeatures = {
    clockIn: Boolean(useFlag("clockIn")),
    pay: Boolean(useFlag("pay")),
  };

  const isDirty = isRowDataDirty(rowData, initialRowData);

  const createRequestInit = () => {
    const token = user?.access_token;
    if (!token || !tenantId) return null;

    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "tenant-id": tenantId,
        "Content-Type": "application/json",
      },
    };
  };

  useEffect(() => {
    const requestInit = createRequestInit();
    if (!requestInit) return;

    const load = async () => {
      const res = await fetch(organisationSettingsReadUrl, requestInit);
      const data = await res.json();
      const mapped = mapSuperAdminSettingsToRowData(data, backstageFeatures);

      setSnapshot(data);
      setRowData(mapped);
      setInitialRowData(mapped.map(r => ({ ...r })));
    };

    load();
  }, [user, organisationSettingsReadUrl, tenantId, backstageFeatures.clockIn, backstageFeatures.pay]);

  const handleSaveChanges = async () => {
    const requestInit = createRequestInit();
    if (!requestInit || !snapshot) return;

    const mapped = mapRowDataToSuperAdminSettings(snapshot, rowData);

    await fetch(`${apiUrl}/api/superadmin/organisation-settings`, {
      method: "PUT",
      ...requestInit,
      body: JSON.stringify(mapped),
    });

    setSnapshot(mapped);
    setInitialRowData(rowData.map(r => ({ ...r })));
  };

  return (
    <GenericSettingsGrid
      rowData={rowData}
      onRowDataChange={setRowData}
      onSave={handleSaveChanges}
      isDirty={isDirty}
    />
  );
};

export default SuperAdminSettingsGrid;
